import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withModifiers as _withModifiers, withKeys as _withKeys, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-4a3e2d4c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "d-flex flex-column align-items-start w-100" }
const _hoisted_2 = { class: "w-100" }
const _hoisted_3 = { class: "d-flex flex-column align-items-start w-100" }
const _hoisted_4 = { class: "d-flex flex-column align-items-start w-100" }
const _hoisted_5 = { class: "d-flex flex-column align-items-start w-100" }
const _hoisted_6 = { class: "d-flex flex-column align-items-start w-100" }
const _hoisted_7 = { class: "d-flex flex-column align-items-start w-100" }

import { computed, nextTick, onMounted, ref, watch } from "vue";
import type { ElInput, ElSelect } from "element-plus";
import DanhMucToaXeSearchInput from "@/components/shared/danh-muc-toa-xe-search-input/DanhMucToaXeSearchInput.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import type { ICapSuaChuaResponseBase } from "@/core/interfaces/ApiResponses";
import DivCol from "@/layout/_shared/DivCol.vue";
import DivRow from "@/layout/_shared/DivRow.vue";

export interface DinhMucCoDinhMetaFormState {
  id_cap_sc: number;
  id_danh_muc_toa_xe: number;
  so_van_ban: string;
  ngay_ban_hanh: Date | string | null;
  ngay_hieu_luc: Date | string | null;
  ngay_het_han: Date | string | null;
  mo_ta: string;
}

type SelectInstance = InstanceType<typeof ElSelect> & {
  focus?: () => void;
  blur?: () => void;
  $el?: HTMLElement;
};

type ComponentWithEl = {
  focus?: () => void;
  $el?: HTMLElement;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'DinhMucCoDinhMetaStep',
  props: {
    form: {},
    capSuaChuaItems: {}
  },
  emits: ["update:form"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

/** Sequential tabindex: nhóm TX → cấp SC → số VB → dates → ghi chú */
const tabIndex = computed(() => {
  let n = 1;

  const danhMucToaXe = n++;
  const capSc = n++;
  const soVanBan = n++;
  const ngayBanHanh = n++;
  const ngayHieuLuc = n++;
  const moTa = n++;

  return {
    danhMucToaXe,
    capSc,
    soVanBan,
    ngayBanHanh,
    ngayHieuLuc,
    moTa,
  };
});

const danhMucToaXeSearchInputRef = ref<{
  focus: () => void;
  blur?: () => void;
} | null>(null);
const capSuaChuaSelectRef = ref<SelectInstance | null>(null);
const soVanBanInputRef = ref<InstanceType<typeof ElInput> | null>(null);
const ngayBanHanhPickerRef = ref<ComponentWithEl | null>(null);
const ngayHieuLucPickerRef = ref<ComponentWithEl | null>(null);
const moTaInputRef = ref<InstanceType<typeof ElInput> | null>(null);

/**
 * el-select puts fallthrough attrs (incl. tabindex) on the root div.
 * Tab then focuses the wrapper (dropdown never opens) while the real
 * input stays tabindex=0 and skips later positive-tabindex fields.
 */
const getComponentRoot = (
  component: { $el?: HTMLElement } | null | undefined
): HTMLElement | null => {
  return component?.$el ?? null;
};

const getInnerFocusable = (
  root: HTMLElement | null
): HTMLInputElement | HTMLTextAreaElement | null => {
  if (!root) {
    return null;
  }

  return (
    root.querySelector<HTMLInputElement | HTMLTextAreaElement>(
      "input.el-input__inner, textarea.el-textarea__inner, input:not([type='hidden']), textarea"
    ) ?? null
  );
};

const syncInnerTabIndex = (
  component: { $el?: HTMLElement } | null | undefined,
  index: number
) => {
  const root = getComponentRoot(component);

  if (!root) {
    return;
  }

  // Keep wrapper out of tab order when attrs leaked onto it.
  if (root.hasAttribute("tabindex")) {
    root.removeAttribute("tabindex");
  }

  const input = getInnerFocusable(root);

  if (!input) {
    return;
  }

  input.tabIndex = index > 0 ? index : -1;
};

const syncAllTabIndexes = async () => {
  await nextTick();

  syncInnerTabIndex(capSuaChuaSelectRef.value, tabIndex.value.capSc);

  // Date pickers: tabindex is applied by the global ElDatePicker wrapper
  // (src/core/plugins/date-picker.ts) via :tabindex — do not sync via $el
  // (picker root is a fragment / popper, so query fails and fields stay at 0).
};

onMounted(() => {
  void syncAllTabIndexes();
});

watch(
  [tabIndex, () => props.capSuaChuaItems.length],
  () => {
    void syncAllTabIndexes();
  },
  { flush: "post" }
);

const blurActiveElement = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

/**
 * EP select calls setSoftFocus() after option select and re-focuses itself.
 * Wait past that before moving focus to the next field.
 */
const afterSelectUiSettled = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 100);
    });
  });

const focusNextAfterSelect = async (focusFn: () => Promise<void>) => {
  await nextTick();
  await afterSelectUiSettled();
  await focusFn();
};

const getVisibleSelectDropdown = (): HTMLElement | null => {
  const dropdowns = Array.from(
    document.querySelectorAll<HTMLElement>(".el-select-dropdown")
  );

  return (
    dropdowns.find((el) => {
      const style = window.getComputedStyle(el);

      return style.display !== "none" && style.visibility !== "hidden";
    }) ?? null
  );
};

const highlightSelectOption = async (options: {
  selectedValue: number;
  selectedLabel?: string;
}) => {
  await nextTick();

  const dropdown = getVisibleSelectDropdown();

  if (!dropdown) {
    return;
  }

  const items = Array.from(
    dropdown.querySelectorAll<HTMLElement>(
      ".el-select-dropdown__item:not(.is-disabled)"
    )
  );

  if (!items.length) {
    return;
  }

  let target: HTMLElement | undefined;

  if (options.selectedValue > 0) {
    target = items.find(
      (el) =>
        el.classList.contains("selected") ||
        el.classList.contains("is-selected")
    );

    if (!target && options.selectedLabel) {
      const label = options.selectedLabel.trim();
      target = items.find((el) => el.textContent?.trim() === label);
    }
  }

  if (!target) {
    target = items[0];
  }

  items.forEach((el) => {
    el.classList.remove("hover", "is-hovering");
  });
  target.classList.add("hover", "is-hovering");
  target.scrollIntoView({ block: "nearest" });
};

const focusSelectInput = async (select: SelectInstance | null) => {
  if (!select) {
    return;
  }

  await nextTick();
  await syncAllTabIndexes();

  // Focus the real input so filterable select opens the menu via handleFocus.
  const input = getInnerFocusable(getComponentRoot(select));

  if (input) {
    input.focus();
    return;
  }

  select.focus?.();
};

const focusDanhMucToaXe = async () => {
  blurActiveElement();
  await nextTick();
  danhMucToaXeSearchInputRef.value?.focus();
};

const focusCapSuaChuaSelect = async () => {
  blurActiveElement();
  await focusSelectInput(capSuaChuaSelectRef.value);
};

const focusSoVanBanInput = async () => {
  blurActiveElement();
  await nextTick();
  soVanBanInputRef.value?.focus();
  soVanBanInputRef.value?.select?.();
};

const focusNgayBanHanhPicker = async () => {
  blurActiveElement();
  await nextTick();
  ngayBanHanhPickerRef.value?.focus?.();
};

const focusNgayHieuLucPicker = async () => {
  blurActiveElement();
  await nextTick();
  ngayHieuLucPickerRef.value?.focus?.();
};

const focusMoTaInput = async () => {
  blurActiveElement();
  await nextTick();
  moTaInputRef.value?.focus();
  moTaInputRef.value?.select?.();
};

const focusFirstField = async () => {
  await focusDanhMucToaXe();
};

const onCapSuaChuaVisibleChange = async (visible: boolean) => {
  await syncAllTabIndexes();

  if (!visible) {
    return;
  }

  const selected = props.capSuaChuaItems.find(
    (item) => item.id === props.form.id_cap_sc
  );

  await highlightSelectOption({
    selectedValue: props.form.id_cap_sc,
    selectedLabel: selected?.ten_cap_sc,
  });
};

const onClearDanhMucToaXe = () => {
  emit("update:form", { id_danh_muc_toa_xe: 0 });
};

const onSelectDanhMucToaXe = async ({ id }: { id: number }) => {
  emit("update:form", { id_danh_muc_toa_xe: id });

  if (id > 0) {
    danhMucToaXeSearchInputRef.value?.blur?.();
    await focusNextAfterSelect(focusCapSuaChuaSelect);
  }
};

const onClearCapSuaChua = () => {
  emit("update:form", { id_cap_sc: 0 });
};

const onSelectCapSuaChua = async (val: number) => {
  const id_cap_sc = Number(val) || 0;
  emit("update:form", { id_cap_sc });

  if (id_cap_sc > 0) {
    // Defer past EP setSoftFocus() which re-focuses the select after change.
    await focusNextAfterSelect(async () => {
      capSuaChuaSelectRef.value?.blur?.();
      blurActiveElement();
      await nextTick();
      await focusSoVanBanInput();
    });
  }
};

const onSelectNgayBanHanh = async (val: Date | string | null) => {
  emit("update:form", { ngay_ban_hanh: val });

  if (val) {
    await focusNextAfterSelect(focusNgayHieuLucPicker);
  }
};

const onSelectNgayHieuLuc = async (val: Date | string | null) => {
  emit("update:form", { ngay_hieu_luc: val });

  if (val) {
    await focusNextAfterSelect(focusMoTaInput);
  }
};

__expose({
  focusFirstField,
  focusDanhMucToaXe,
});

return (_ctx: any,_cache: any) => {
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!

  return (_openBlock(), _createElementBlock("div", {
    class: "dinh-muc-co-dinh-meta-step",
    onFocusin: syncAllTabIndexes
  }, [
    _createVNode(DivRow, { class: "mb-3 g-3" }, {
      default: _withCtx(() => [
        _createVNode(DivCol, {
          "additional-class": "pb-4",
          cols: 3
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createVNode(LabelCustom, {
                isRequired: "",
                class: "dmcd-form-label-clickable",
                onClick: focusDanhMucToaXe
              }, {
                default: _withCtx(() => [
                  _createTextVNode(" Chọn nhóm toa xe ")
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_2, [
                _createVNode(DanhMucToaXeSearchInput, {
                  ref_key: "danhMucToaXeSearchInputRef",
                  ref: danhMucToaXeSearchInputRef,
                  "full-width": "",
                  id: _ctx.form.id_danh_muc_toa_xe,
                  "prop-name": "id_danh_muc_toa_xe",
                  tabindex: tabIndex.value.danhMucToaXe,
                  onClear: onClearDanhMucToaXe,
                  onSelect: onSelectDanhMucToaXe
                }, null, 8, ["id", "tabindex"])
              ])
            ])
          ]),
          _: 1
        }),
        _createVNode(DivCol, {
          "additional-class": "pb-4",
          cols: 3
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(LabelCustom, {
                isRequired: "",
                class: "dmcd-form-label-clickable",
                onClick: focusCapSuaChuaSelect
              }, {
                default: _withCtx(() => [
                  _createTextVNode(" Chọn cấp sửa chữa ")
                ]),
                _: 1
              }),
              _createVNode(_component_el_form_item, {
                class: "mb-0 w-full",
                prop: "id_cap_sc"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_select, {
                    ref_key: "capSuaChuaSelectRef",
                    ref: capSuaChuaSelectRef,
                    "model-value": _ctx.form.id_cap_sc,
                    class: "w-full",
                    filterable: "",
                    "default-first-option": "",
                    name: "id_cap_sc",
                    placeholder: "Chọn cấp sửa chữa",
                    onClear: onClearCapSuaChua,
                    onChange: onSelectCapSuaChua,
                    onVisibleChange: onCapSuaChuaVisibleChange
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_option, {
                        disabled: "",
                        label: "Chọn dữ liệu",
                        value: 0
                      }),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.capSuaChuaItems, (item) => {
                        return (_openBlock(), _createBlock(_component_el_option, {
                          key: item.id,
                          value: item.id,
                          label: item.ten_cap_sc
                        }, null, 8, ["value", "label"]))
                      }), 128))
                    ]),
                    _: 1
                  }, 8, ["model-value"])
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        }),
        _createVNode(DivCol, {
          "additional-class": "pb-4",
          cols: 3
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(LabelCustom, {
                isRequired: "",
                class: "dmcd-form-label-clickable",
                onClick: focusSoVanBanInput
              }, {
                default: _withCtx(() => [
                  _createTextVNode(" Số văn bản ")
                ]),
                _: 1
              }),
              _createVNode(_component_el_form_item, {
                class: "mb-0 w-full",
                prop: "so_van_ban"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_input, {
                    ref_key: "soVanBanInputRef",
                    ref: soVanBanInputRef,
                    "model-value": _ctx.form.so_van_ban,
                    class: "w-full",
                    maxlength: "100",
                    name: "so_van_ban",
                    placeholder: "Số văn bản",
                    "show-word-limit": "",
                    tabindex: tabIndex.value.soVanBan,
                    onFocus: _cache[0] || (_cache[0] = ($event: any) => (($event.target as HTMLInputElement).select())),
                    onKeydown: _withKeys(_withModifiers(focusNgayBanHanhPicker, ["prevent"]), ["enter"]),
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = 
                (val) => _ctx.$emit('update:form', { so_van_ban: val })
              )
                  }, null, 8, ["model-value", "tabindex", "onKeydown"])
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(DivRow, { class: "mb-3 g-3" }, {
      default: _withCtx(() => [
        _createVNode(DivCol, {
          "additional-class": "pb-4",
          cols: 3
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(LabelCustom, {
                isRequired: "",
                class: "dmcd-form-label-clickable",
                onClick: focusNgayBanHanhPicker
              }, {
                default: _withCtx(() => [
                  _createTextVNode(" Ngày ban hành ")
                ]),
                _: 1
              }),
              _createVNode(_component_el_form_item, {
                class: "mb-0 w-full",
                prop: "ngay_ban_hanh"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_date_picker, {
                    ref_key: "ngayBanHanhPickerRef",
                    ref: ngayBanHanhPickerRef,
                    "model-value": _ctx.form.ngay_ban_hanh,
                    "allow-before-global-min": "",
                    class: "w-100",
                    placeholder: "Ngày ban hành",
                    name: "ngay_ban_hanh",
                    format: "DD/MM/YYYY",
                    tabindex: tabIndex.value.ngayBanHanh,
                    "onUpdate:modelValue": onSelectNgayBanHanh
                  }, null, 8, ["model-value", "tabindex"])
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        }),
        _createVNode(DivCol, {
          "additional-class": "pb-4",
          cols: 3
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_6, [
              _createVNode(LabelCustom, {
                isRequired: "",
                class: "dmcd-form-label-clickable",
                onClick: focusNgayHieuLucPicker
              }, {
                default: _withCtx(() => [
                  _createTextVNode(" Ngày hiệu lực ")
                ]),
                _: 1
              }),
              _createVNode(_component_el_form_item, {
                class: "mb-0 w-full",
                prop: "ngay_hieu_luc"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_date_picker, {
                    ref_key: "ngayHieuLucPickerRef",
                    ref: ngayHieuLucPickerRef,
                    "model-value": _ctx.form.ngay_hieu_luc,
                    class: "w-100",
                    placeholder: "Ngày hiệu lực",
                    name: "ngay_hieu_luc",
                    format: "DD/MM/YYYY",
                    tabindex: tabIndex.value.ngayHieuLuc,
                    "onUpdate:modelValue": onSelectNgayHieuLuc
                  }, null, 8, ["model-value", "tabindex"])
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        }),
        _createVNode(DivCol, {
          "additional-class": "pb-4",
          cols: 6
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_7, [
              _createVNode(LabelCustom, {
                class: "dmcd-form-label-clickable",
                onClick: focusMoTaInput
              }, {
                default: _withCtx(() => [
                  _createTextVNode(" Ghi chú ")
                ]),
                _: 1
              }),
              _createVNode(_component_el_form_item, {
                class: "mb-0 w-full",
                prop: "mo_ta"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_input, {
                    ref_key: "moTaInputRef",
                    ref: moTaInputRef,
                    "model-value": _ctx.form.mo_ta,
                    class: "w-full",
                    name: "mo_ta",
                    placeholder: "Ghi chú",
                    type: "textarea",
                    autosize: { minRows: 1 },
                    resize: "none",
                    tabindex: tabIndex.value.moTa,
                    onFocus: _cache[2] || (_cache[2] = ($event: any) => (($event.target as HTMLInputElement).select())),
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = 
                (val) => _ctx.$emit('update:form', { mo_ta: val })
              )
                  }, null, 8, ["model-value", "tabindex"])
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ], 32))
}
}

})